import { useMemo } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useTenantID } from '@/hooks/useTenantID';
import Button from '../core/Button';
import { StatusIndicator } from '@/components/StatusIndicator';
import { IconEdit } from '@/icons';
import { ClaimStateBadges } from '@/components/Students/ClaimStateBadges';
import type { TravellerStatus } from '@/components/StatusIndicator';
import { STUDENT_TYPE_VIEW } from '@/helpers/collectionTypes';
import type { FormattedStudent, StudentType } from '@/types/students';
import { getMembershipStatus } from '@/helpers/membershipStatus';
import { scheduledClaimCount } from '@/helpers/scheduledClaims';
import type { CollectionTicketClaimMemberStateDTO } from '@/api/trafikportalen';

interface StudentListItemProps {
  student: FormattedStudent;
  studentType: StudentType;
  claimStates?: CollectionTicketClaimMemberStateDTO[];
  activeClaimCount?: number;
}

export const StudentListItem = ({
  student,
  studentType,
  claimStates,
  activeClaimCount = 0,
}: StudentListItemProps) => {
  const tenantId = useTenantID();
  const { formatMessage } = useIntl();

  const status: TravellerStatus = useMemo(
    () =>
      getMembershipStatus({
        active: student.active,
        inactivate_at: student.inactivate_at,
      }),
    [student.active, student.inactivate_at],
  );

  const manualEditDescription = formatMessage({
    id: 'import.manuallyModified.description',
    defaultMessage: 'Denna elev har ändrats manuellt sedan senaste importen',
  });
  const recordedCount = claimStates?.length ?? 0;
  const scheduledCount = scheduledClaimCount(
    activeClaimCount,
    recordedCount,
    student,
  );

  return (
    <Button
      to="/$tenantId/travellers/$travellerId/$view"
      params={{
        tenantId,
        travellerId: student.traveller_id,
        view: STUDENT_TYPE_VIEW[studentType],
      }}
      className="w-full flex-col items-start gap-1 py-2 font-normal"
    >
      <div className="flex w-full flex-wrap items-center gap-2">
        <StatusIndicator status={status} inactivateAt={student.inactivate_at} />
        <span className="text-nowrap font-mono text-text-800">
          {student.national_identity_number}
        </span>
        <span className="truncate">
          {student.first_name} {student.last_name}
        </span>
        {student.manually_modified && (
          <span
            className="flex items-center gap-1 rounded-full bg-yellow-500 px-2 py-0.5 text-sm font-bold text-contrast-yellow"
            title={manualEditDescription}
          >
            <IconEdit className="size-3" aria-hidden="true" />
            <span>
              <FormattedMessage
                id="import.manuallyModified.title"
                defaultMessage="Manuellt ändrad"
              />
            </span>
            <span className="sr-only">. {manualEditDescription}</span>
          </span>
        )}
        {student.school_name && (
          <span className="ml-auto truncate text-text-800">
            {student.school_name}
          </span>
        )}
      </div>
      {(scheduledCount > 0 || recordedCount > 0) && (
        <ClaimStateBadges
          claimStates={claimStates ?? []}
          scheduledCount={scheduledCount}
          inactivateAt={
            status === 'will-inactivate' ? student.inactivate_at : undefined
          }
          className="w-full"
        />
      )}
    </Button>
  );
};
